import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import IconButton from '@material-ui/core/IconButton';
import Typography from '@material-ui/core/Typography';
import CloseIcon from '@material-ui/icons/Close';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import CircularProgress from '@material-ui/core/CircularProgress';
import { InputAdornment } from "@material-ui/core";
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';

import { useDispatch, useSelector } from "react-redux";
import allActions from "../../../actions";

import MyTextField from '../../utils/MyTextField';
import MyTable from '../../utils/MyTable';
import UnoButton from '../../utils/UnoButton';
import ConfirmActionModal from '../../modals/ConfirmActionModal';
import { checkUserIsRole, roles } from '../../utils/user.js';
import { parsePercentage } from '../../utils/utils';
import {
    RATE_FIELDS,
    RATE_STATUS,
    RATE_STATUS_LABEL,
    formatRate,
    getRateTitle,
    parseManualRate,
} from '../../utils/costsUtils';
import { unoTheme } from "../../../assets/styles/unoTheme";
import { ClientFundRatesAPI } from '../../../API/client_fund_rates';

const useStyles = makeStyles((theme) => ({
    root: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
    },

    header: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: theme.spacing(3),
    },

    main: {
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        paddingLeft: theme.spacing(5),
        paddingRight: theme.spacing(5),
        textAlign: 'left',
        overflowY: 'auto',
    },

    infoText: {
        fontSize: '0.8rem',
        color: unoTheme.defaultTextColor,
        textAlign: 'center',
        marginTop: theme.spacing(2),
        fontStyle: 'italic',
    },

    rateRow: {
        '& > *': {
            borderBottom: 'unset',
            fontSize: '0.85rem',
        },
        '&:nth-of-type(odd)': {
            backgroundColor: unoTheme.secondaryTuplaTable,
        },
    },

    manualInput: {
        width: '140px',
    },

    bottomButtons: {
        paddingTop: theme.spacing(2),
        paddingBottom: theme.spacing(2),
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: theme.spacing(1),
    },

    rightDivBackdrop: {
        backgroundColor: 'white',
        position: 'absolute',
        opacity: 0.7,
        top: 0,
        bottom: 0,
        right: 0,
        left: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',

        '& .progress': {
            color: unoTheme.mainColor
        }
    },

    hiddenIcon: {
        visibility: 'hidden',
    },
}));

//O pg devolve NUMERIC como "1.5000": exibe com vírgula e sem zeros à direita
function toInputValue(value) {
    const num = parseManualRate(value);
    return num === null ? '' : String(num).replace('.', ',');
}

//Só dígitos e uma vírgula, com até 4 casas (precisão de NUMERIC(10,4))
function sanitizeRateInput(value) {
    const cleaned = String(value || '').replace('.', ',').replace(/[^0-9,]/g, '');
    const [integer, ...decimals] = cleaned.split(',');
    if (decimals.length === 0) return integer;
    return integer + ',' + decimals.join('').slice(0, 4);
}

function buildForm(manualRates) {
    const form = {};
    RATE_FIELDS.forEach(({ field }) => {
        form[field] = toInputValue(manualRates ? manualRates[field] : null);
    });
    return form;
}

//Taxa como a fonte informaria, sem o ajuste manual
function getSourceRate(rate) {
    if (!rate) return null;
    return rate.manual ? rate.apiRate : rate;
}

function formatSourceRate(rate) {
    if (!rate) return RATE_STATUS_LABEL[RATE_STATUS.NOT_INFORMED];
    if (rate.status === RATE_STATUS.INFORMED) return formatRate(rate.value);
    return RATE_STATUS_LABEL[rate.status] || RATE_STATUS_LABEL[RATE_STATUS.NOT_INFORMED];
}

export default function ClientFundRatesEdit(props) {

    const classes = useStyles();
    const dispatch = useDispatch();

    const { content } = props;
    const clientOnScreen = content?.clientOnScreen;
    const clientId = clientOnScreen?.id;
    const clientName = clientOnScreen?.rpps_name || clientOnScreen?.rpps_name_initial || '';
    const fundRow = content?.data || {};
    const fundId = fundRow.fund_id;

    const currentUser = useSelector(state => state.currentUser);

    //Ajuste manual só pelo pessoal da LEMA; o servidor aplica a mesma restrição
    const canEdit = checkUserIsRole(currentUser, [roles.superadmin, roles.admin, roles.advisor]);

    const [openBackdrop, setOpenBackdrop] = useState(false);
    //Ajustes gravados no banco (linha de client_fund_rates) — decide se há o que remover
    const [savedRates, setSavedRates] = useState(fundRow.manualRates || null);
    const [form, setForm] = useState(() => buildForm(fundRow.manualRates));
    const [invalidFields, setInvalidFields] = useState([]);
    const [confirmRemove, setConfirmRemove] = useState(false);

    const hasSavedRates = !!savedRates && RATE_FIELDS.some(({ field }) => parseManualRate(savedRates[field]) !== null);

    function showAlert(type, message) {
        dispatch(allActions.mainAlertActions.setMainAlert({ show: true, type, message }));
    }

    //A tela de Custos remonta a linha do fundo assim que o ajuste é gravado, com o painel aberto
    function notifySaved(manualRates) {
        dispatch(allActions.rightContentActions.setAction({
            task: 'updateFundRates',
            data: { fundId, manualRates },
        }));
    }

    function handleChange(field, value) {
        setForm({ ...form, [field]: sanitizeRateInput(value) });
        setInvalidFields(invalidFields.filter(f => f !== field));
    }

    async function handleSave() {
        const data = {};
        const invalid = [];
        RATE_FIELDS.forEach(({ field }) => {
            const input = form[field];
            if (!input) {
                data[field] = null;
                return;
            }
            const value = parsePercentage(input);
            if (!Number.isFinite(value) || value < 0 || value > 100) {
                invalid.push(field);
                return;
            }
            data[field] = value;
        });

        if (invalid.length > 0) {
            setInvalidFields(invalid);
            showAlert('warning', 'As taxas devem ser percentuais entre 0 e 100');
            return;
        }

        setOpenBackdrop(true);

        const response = await ClientFundRatesAPI.upsert(clientId, fundId, data);

        if (response.success) {
            const saved = response.body?.rows?.[0] || null;
            setSavedRates(saved);
            setForm(buildForm(saved));
            notifySaved(saved);
            showAlert('success', saved ? 'Taxas ajustadas com sucesso!' : 'Ajustes manuais removidos');
        } else {
            showAlert('error', 'Erro ao salvar taxas: ' + (response.body?.message || 'Erro desconhecido'));
        }

        setOpenBackdrop(false);
    }

    async function handleRemoveAction(confirmed) {
        setConfirmRemove(false);

        if (!confirmed) return;

        setOpenBackdrop(true);

        const response = await ClientFundRatesAPI.remove(clientId, fundId);

        if (response.success) {
            setSavedRates(null);
            setForm(buildForm(null));
            setInvalidFields([]);
            notifySaved(null);
            showAlert('success', 'Ajustes manuais removidos');
        } else {
            showAlert('error', 'Erro ao remover ajustes: ' + (response.body?.message || 'Erro desconhecido'));
        }

        setOpenBackdrop(false);
    }

    function handleClose() {
        dispatch(allActions.rightContentActions.setRightContent(null));
    }

    return (
        <div className={classes.root}>
            <div className={classes.header}>
                <IconButton className={classes.hiddenIcon}>
                    <ArrowBackIcon />
                </IconButton>

                <div style={{ textAlign: 'center' }}>
                    <Typography variant={'h6'}>
                        {'Taxas do Fundo'}
                    </Typography>
                    {
                        clientName ?
                            <Typography variant={'subtitle2'} style={{ color: unoTheme.defaultTextColor, textTransform: 'uppercase' }}>
                                {clientName}
                            </Typography> : null
                    }
                </div>

                <IconButton onClick={handleClose}>
                    <CloseIcon />
                </IconButton>
            </div>

            <div className={classes.main}>

                <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                    {fundRow.fund_name || '-'}
                </Typography>
                <Typography variant="body2" style={{ color: unoTheme.defaultTextColor, marginBottom: '16px' }}>
                    {fundRow.cnpj || '-'}
                </Typography>

                <MyTable size="small" sizeheaders="small">
                    <TableHead>
                        <TableRow>
                            <TableCell align="left">Taxa</TableCell>
                            <TableCell align="center">Fonte de dados</TableCell>
                            <TableCell align="center">Ajuste manual</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {RATE_FIELDS.map(({ key, field, label }) => {
                            const sourceRate = getSourceRate(fundRow.rates && fundRow.rates[key]);
                            return (
                                <TableRow key={'fund-rate-' + field} className={classes.rateRow}>
                                    <TableCell align="left">{label}</TableCell>
                                    <TableCell
                                        align="center"
                                        title={sourceRate ? getRateTitle(sourceRate.status, sourceRate.reason) : ''}>
                                        {formatSourceRate(sourceRate)}
                                    </TableCell>
                                    <TableCell align="center">
                                        <MyTextField
                                            id={'manual-rate-' + field}
                                            className={classes.manualInput}
                                            align={'right'}
                                            endadornment={<InputAdornment position="end">%</InputAdornment>}
                                            value={form[field]}
                                            onChange={(value) => handleChange(field, value)}
                                            placeholder="usar fonte"
                                            size="small"
                                            disabled={!canEdit}
                                            validator={invalidFields.includes(field) ? true : undefined}
                                            inputProps={{ maxLength: 9 }}
                                        />
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </MyTable>

                <Typography className={classes.infoText}>
                    Obs.: A taxa preenchida prevalece sobre a fonte de dados para este cliente, em qualquer período.
                    Deixe em branco para usar a fonte.
                </Typography>

                {
                    canEdit ?
                        <div className={classes.bottomButtons}>
                            {
                                hasSavedRates ?
                                    <UnoButton
                                        onClick={() => setConfirmRemove(true)}
                                        type='default'>
                                        Remover ajustes
                                    </UnoButton> : null
                            }
                            <UnoButton
                                onClick={handleSave}
                                type='primary'
                                variant="contained">
                                Salvar
                            </UnoButton>
                        </div> : null
                }

            </div>

            {openBackdrop ? (
                <div className={classes.rightDivBackdrop}>
                    <CircularProgress className={'progress'} />
                </div>
            ) : null}

            <ConfirmActionModal
                open={confirmRemove}
                title={'Remover ajustes manuais'}
                message={
                    <span>
                        {'Remover todos os ajustes manuais de taxas de '}
                        <strong>{fundRow.fund_name || 'este fundo'}</strong>
                        {'? As taxas voltam a vir da fonte de dados.'}
                    </span>
                }
                confirmText={'Remover'}
                handleAction={(confirmed) => handleRemoveAction(confirmed)} />
        </div>
    );
}
